<script setup lang="ts">
import { computed, useAttrs, useId } from "vue";

const attrs = useAttrs();
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  reverseMode: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["update:modelValue"]);
// Bound after $attrs: a caller's id also lands on the root div, which would capture the label's `for`.
const inputId = useId();
const isDisabled = computed(() => !!attrs.disabled);
const isReadOnly = computed(() => !!attrs["read-only"] || !!attrs.readonly);

const checkbox = computed({
  get() {
    return props.reverseMode ? !props.modelValue : props.modelValue;
  },
  set(val) {
    if (isReadOnly.value) return;
    emit("update:modelValue", props.reverseMode ? !val : val);
  },
});

const handleClick = (event: MouseEvent) => {
  if (isReadOnly.value) {
    event.preventDefault();
  }
};
</script>

<template>
  <div class="flex">
    <div class="switch-container">
      <div>
        <input
          v-bind="$attrs"
          :id="inputId"
          v-model="checkbox"
          type="checkbox"
          class="switch-checkbox"
          @click="handleClick"
        />
        <span class="switch-label" :data-disabled="isDisabled" :data-readonly="isReadOnly"></span>
      </div>
    </div>
    <label
      v-if="label"
      :for="inputId"
      class="text-secondary-color body_regular"
      :class="isDisabled || isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'"
      >{{ label }}</label
    >
  </div>
</template>

<style scoped>
.switch-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 36px;
  height: 20px;
  margin-right: 8px;
}

.switch-checkbox {
  opacity: 0;
  height: 20px;
  width: 36px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  &[read-only="true"],
  &[readonly="true"] {
    pointer-events: none;
  }
}

.switch-label[data-readonly="true"] {
  cursor: not-allowed;
}

.switch-label[data-disabled="true"] {
  cursor: not-allowed;
}

.switch-label {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--grey-medium-light);
  border-radius: 18px;
  cursor: pointer;
  transition: background-color 0.3s;
  pointer-events: none;
}

.switch-label::before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: var(--white-main);
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.switch-checkbox:checked + .switch-label {
  &[data-disabled="false"] {
    background-color: var(--green-main);
  }
  &[data-disabled="true"] {
    background-color: #b6f1d8;
  }
}

.switch-checkbox:checked + .switch-label::before {
  transform: translateX(16px);
}

.switch-checkbox:hover:checked + .switch-label {
  &[data-disabled="false"] {
    background-color: #00ce81;
  }
}

.switch-checkbox:hover + .switch-label {
  &[data-disabled="false"] {
    background-color: var(--grey-medium);
  }
}

.switch-checkbox + .switch-label {
  &[data-disabled="true"] {
    background-color: var(--grey-lightest);
  }
}
</style>
